import { useEffect, useMemo } from "react"
import { Location } from "react-router"

type BlockerResult = boolean | string
type BlockerFunction = (nextLocation: Location) => BlockerResult
type Blocker = BlockerResult | BlockerFunction

/**
 * Keep track of latest index to keep them unique.
 */
let latestBlockerIndex = 0

/**
 * A global store for blocker functions.
 *
 * Using a provider or other state management could cause re-render loops.
 */
export const blockers = new Map<number, BlockerFunction>()

/**
 * A hook that allows components to block navigation when certain conditions are met.
 *
 * Unlike the `useBlocker` hook from `react-router`, this hook allows multiple components to block navigation at the same time.
 *
 * You probably want to be using the `useUnsavedChanges` instead of this hook directly, as it also blocks page unloads and is more user-friendly.
 *
 * @param when - A boolean, a string, or a function that returns either of those. If it's a boolean, it will block navigation when true. If it's a string, it will block navigation and show the string as a confirmation message. If it's a function, it will be called with the next location and should return either a boolean or a string to determine whether to block navigation.
 */
export const useBlocker = (when: Blocker) => {
  const uniqueId = useMemo(() => latestBlockerIndex++, [])

  useEffect(() => {
    // Store the blocker at a unique index
    blockers.set(uniqueId, typeof when === "function" ? when : () => when)
    return () => {
      blockers.delete(uniqueId)
    }
  }, [when, uniqueId])
}
